import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { FormError } from '@/components/FormError'
import { Choice, Kept, LookTile, PageEdit, Section, YourLook, type Trim } from '@/components/print/Builder'
import { CardBuilder, type Card } from '@/components/print/CardDeck'
import { MakeFiles } from '@/components/print/MakeFiles'
import { MarkedPage } from '@/components/print/MarkedPage'
import {
  CARD_LOOK_IDS,
  CARD_LOOKS,
  CARD_SIZES,
  readChosenCards,
  sameCardLook,
  type CardEdit,
  type CardEdits,
  type CardLook,
  type CardSize,
  type ChosenCards,
} from '@/lib/cookbook/cardLooks'
import type { CardsPreview } from '@/lib/cookbook/cards'
import { useCookbook, type Cookbook } from '@/lib/cookbooks'
import { usePageFrame } from '@/lib/frame'
import { editPage, useDesign } from '@/lib/print/design'
import { ScreenLoader } from '@/components/Loader'

export function PrintCardsPage() {
  const { id = '' } = useParams()
  const cookbook = useCookbook(id)
  if (cookbook.isPending) return <ScreenLoader surface="table" />
  if (cookbook.isError) return <FormError error={cookbook.error} />
  return <Builder cookbook={cookbook.data} />
}

// The cards in a look, drawn by the PDF's own code on marks (cookbook/cards.ts), once its fonts have loaded.
function usePreview(cookbook: Cookbook, size: CardSize, looks: CardLook[], edits?: CardEdits) {
  const [state, setState] = useState<{ previews: CardsPreview[] } | { error: Error }>()
  const key = JSON.stringify([size.id, looks, edits])
  useEffect(() => {
    let live = true
    import('@/lib/cookbook/cards')
      .then(({ cardsPreview }) => Promise.all(looks.map((look) => cardsPreview(cookbook, size, look, edits))))
      .then(
        (previews) => live && setState({ previews }),
        (e: unknown) => {
          console.error(e)
          if (live) setState({ error: new Error('The cards couldn’t be drawn. Please reload to try again.') })
        },
      )
    return () => {
      live = false
    }
    // The key stands for the size, looks and edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookbook, key])
  // The last cards drawn stay up while a change is drawn, so the pile doesn't blink.
  return state
}

// The recipe card builder: pick a look, change anything about it, and flip through the cards that will be printed,
// one for each recipe in the cookbook. The cards are drawn by the same code that draws the PDF.
function Builder({ cookbook }: { cookbook: Cookbook }) {
  usePageFrame(cookbook.familyId, {
    title: 'Recipe cards',
    back: { to: `/cookbook/${cookbook.id}`, label: 'Back to the cookbook' },
  })
  // With no photos of cards among the recipes, the dish is the place to start.
  const cards = cookbook.pages.some((x) => x.recipe.photos.some((p) => p.of === 'card'))
  const start = cards ? 'original' : 'dish'
  const {
    design: chosen,
    choose,
    keeping,
  } = useDesign<ChosenCards>({
    book: { kind: 'cookbook', id: cookbook.id },
    what: 'cards',
    storageKey: `gf.cards-look.${cookbook.id}`,
    kept: cookbook.cardsDesign ?? null,
    mine: cookbook.mine,
    read: readChosenCards,
    start: { from: start, look: CARD_LOOKS[start].look },
  })
  const [sizeId, setSizeId] = useState<CardSize['id']>('4x6')
  const [busy, setBusy] = useState(false)
  const size = CARD_SIZES.find((x) => x.id === sizeId)!
  const look = chosen.look
  const trim = size.trim
  const custom = !sameCardLook(look, CARD_LOOKS[chosen.from].look)
  const drawn = usePreview(cookbook, size, [look], chosen.pages)
  const tiles = usePreview(
    cookbook,
    size,
    CARD_LOOK_IDS.map((id) => CARD_LOOKS[id].look),
  )

  const change = <K extends keyof CardLook>(key: K, value: CardLook[K]) =>
    choose({ ...chosen, look: { ...look, [key]: value } })
  // A recipe's card's own choices, kept by the recipe's id.
  const editOne = (id: string, edit: CardEdit) => choose({ ...chosen, pages: editPage(chosen.pages, id, edit) })

  const preview = drawn && 'previews' in drawn ? drawn.previews[0]! : null
  const shown = preview?.look ?? look
  const titles = new Map(cookbook.pages.map((x) => [x.recipe.id, x.recipe.title]))
  const deck: Card[] = (preview?.cards ?? []).map((card) => ({
    name: titles.get(card.recipe) ?? '',
    front: () => <MarkedPage marks={card.front} trim={trim} paper={shown.paper} />,
    back: () => <MarkedPage marks={card.back} trim={trim} paper={shown.paper} />,
  }))
  const count = preview?.cards.length ?? cookbook.pages.length
  const long = (preview?.long ?? []).map((id) => titles.get(id) ?? '')

  return (
    <CardBuilder
      label={`Recipe cards from ${cookbook.title}`}
      badge="Recipe cards"
      back={{ to: `/cookbook/${cookbook.id}`, label: 'the cookbook' }}
      trim={trim}
      photos={look.photos}
      cards={deck}
      thisCard={(at) => {
        const id = preview?.cards[at]?.recipe
        const page = cookbook.pages.find((x) => x.recipe.id === id)
        if (!page) return null
        const edit = chosen.pages?.[page.recipe.id] ?? {}
        const has = (of: 'card' | 'food') => page.recipe.photos.some((x) => x.of === of)
        const fronts: [CardLook['front'] | '', string][] = [['', 'As the set']]
        if (has('card')) fronts.push(['card', 'Original card'])
        if (page.picture || has('food')) fronts.push(['dish', 'Dish photo'])
        fronts.push(['title', 'Name'])
        return (
          <PageEdit
            name={page.recipe.title}
            edited={Boolean(chosen.pages?.[page.recipe.id])}
            onReset={() => editOne(page.recipe.id, {})}
          >
            <Choice<CardLook['front'] | ''>
              label="Front"
              value={edit.front ?? ''}
              options={fronts}
              onChange={(v) => editOne(page.recipe.id, { ...edit, front: v || undefined })}
            />
            <Choice<CardLook['edges'] | ''>
              label="Edges"
              value={edit.edges ?? ''}
              options={[
                ['', 'As the set'],
                ['bleed', 'Edge to edge'],
                ['border', 'Border'],
              ]}
              onChange={(v) => editOne(page.recipe.id, { ...edit, edges: v || undefined })}
            />
          </PageEdit>
        )
      }}
    >
      {drawn && 'error' in drawn && (
        <div className="px-5 pt-5">
          <FormError error={drawn.error} />
        </div>
      )}
      <Section title="Start with a look">
        <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label="Looks">
          {CARD_LOOK_IDS.map((id, i) => (
            <LookTile
              key={id}
              name={CARD_LOOKS[id].name}
              blurb={CARD_LOOKS[id].blurb}
              checked={chosen.from === id && !custom}
              onPick={() => choose({ ...chosen, from: id, look: CARD_LOOKS[id].look })}
            >
              {tiles && 'previews' in tiles && (
                <Sample preview={tiles.previews[i]!} look={CARD_LOOKS[id].look} trim={trim} />
              )}
            </LookTile>
          ))}
        </div>
        {custom && (
          <YourLook
            from={CARD_LOOKS[chosen.from].name}
            onReset={() => choose({ ...chosen, look: CARD_LOOKS[chosen.from].look })}
          />
        )}
        <Kept mine={cookbook.mine} keeping={keeping} noun="cookbook" by={cookbook.by} />
      </Section>

      <Section title="Customize">
        <Choice
          label="Front"
          value={look.front}
          options={[
            ['card', 'Original card'],
            ['dish', 'Dish photo'],
            ['title', 'Name'],
          ]}
          onChange={(v) => change('front', v)}
        />
        <Choice
          label="Edges"
          value={look.edges}
          options={[
            ['bleed', 'Edge to edge'],
            ['border', 'Border'],
          ]}
          onChange={(v) => change('edges', v)}
        />
        <Choice
          label="Type"
          value={look.type}
          options={[
            ['traditional', 'Traditional'],
            ['modern', 'Modern'],
          ]}
          onChange={(v) => change('type', v)}
        />
        <Choice
          label="Paper"
          value={look.paper}
          options={[
            ['white', 'White'],
            ['natural', 'Natural'],
          ]}
          onChange={(v) => change('paper', v)}
        />
        <Choice
          label="Photo color"
          hint="How the photos print. The photos in the app stay as they are."
          value={look.photos}
          options={[
            ['color', 'Color'],
            ['mono', 'Black and white'],
            ['sepia', 'Sepia'],
          ]}
          onChange={(v) => change('photos', v)}
        />
        <p className="text-sub text-muted-foreground">
          {look.edges === 'bleed'
            ? 'Photos are cropped to fill the front. The photos in your recipes stay as they are.'
            : 'A photo of a card is shown whole, so none of the writing is cut off.'}{' '}
          A recipe without the photo asked for shows the other one, or its name.
        </p>
      </Section>

      <Section title="Size">
        <Choice
          label="Card size"
          value={sizeId}
          disabled={busy}
          options={CARD_SIZES.map((x) => [x.id, x.label] as [CardSize['id'], string])}
          onChange={setSizeId}
        />
        <p className="text-sub text-muted-foreground">
          {count} {count === 1 ? 'card' : 'cards'}, printed on both sides.
          {long.length > 0 && ` ${listed(long)} ${long.length === 1 ? 'runs' : 'run'} on to a second card.`} Each page
          of the PDF is one side of a card, front then back, with ⅛ in extra on every edge for the cut, as print shops
          ask.
        </p>
        <MakeFiles
          key={`${sizeId}:${JSON.stringify(chosen)}`}
          book={{
            title: cookbook.title,
            interior: async () => (await import('@/lib/cookbook/cards')).cardsPdf(cookbook, size, look, chosen.pages),
          }}
          formatId="pdf"
          part="cards"
          describe={(pages) => `The PDF has ${pages} pages: ${pages / 2} cards, front and back.`}
          filler=""
          onBusy={setBusy}
        />
      </Section>
    </CardBuilder>
  )
}

const listed = (names: string[]) =>
  names.length <= 3 ? names.join(', ') : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`

// A look's tile: the first card's front in it.
function Sample({ preview, look, trim }: { preview: CardsPreview; look: CardLook; trim: Trim }) {
  const first = preview.cards[0]
  return (
    <span
      className="relative block h-[70%] overflow-hidden rounded-[2px] shadow-paper"
      style={{ aspectRatio: `${trim.width} / ${trim.height}` }}
    >
      <MarkedPage marks={first?.front ?? []} trim={trim} paper={look.paper} />
    </span>
  )
}
